/**
 * WS Order — standalone wholesale ordering page.
 *
 * One in-memory order-state store is the single source of truth for every
 * piece of UI on this page: product cards, the floating order bar, the
 * Order Summary, and the shared wholesale product modal (opened via
 * "+ Details", reusing assets/wholesale-product-modal.js's adapter hook so
 * it writes here instead of the real Shopify cart). No Shopify cart, no
 * Draft Order, no external database — per instruction, all of that is
 * deferred to a later step.
 */

// ---------------------------------------------------------------------
// Order state — single source of truth
// ---------------------------------------------------------------------

/** @typedef {{ variantId: string, quantity: number, unitPriceCents: number, sku: string, productTitle: string, productHandle: string, productUrl: string, finish: string, size: string, image: string }} OrderLine */

/** @type {Map<string, OrderLine>} */
const orderLines = new Map();
/** @type {Set<() => void>} */
const subscribers = new Set();

// Cart contents only (no personal info) — persisted to localStorage so
// selections survive the Wholesale Order Request form's real Shopify
// submission round-trip (see initOrderForm below), and are only ever
// cleared once that submission is confirmed successful.
const ORDER_LINES_STORAGE_KEY = 'wsOrderLines';

function persistOrderLines() {
  try {
    localStorage.setItem(ORDER_LINES_STORAGE_KEY, JSON.stringify(Array.from(orderLines.entries())));
  } catch (error) {
    // Storage can be unavailable (private browsing, quota, etc.) — the
    // cart still works for the current page view either way.
  }
}

function restoreOrderLines() {
  try {
    const raw = localStorage.getItem(ORDER_LINES_STORAGE_KEY);
    if (!raw) return;
    /** @type {Array<[string, OrderLine]>} */
    const entries = JSON.parse(raw);
    entries.forEach(([variantId, line]) => orderLines.set(variantId, line));
  } catch (error) {
    // Ignore corrupt/unavailable storage — just start with an empty cart.
  }
}

function clearPersistedOrderLines() {
  try {
    localStorage.removeItem(ORDER_LINES_STORAGE_KEY);
  } catch (error) {
    // Nothing to do if storage isn't available.
  }
}

function notify() {
  persistOrderLines();
  subscribers.forEach((fn) => fn());
}

/**
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/**
 * @param {Omit<OrderLine, 'quantity'>} meta
 * @param {number} quantity
 */
function setLineQuantity(meta, quantity) {
  const variantId = String(meta.variantId);
  if (!quantity || quantity <= 0) {
    orderLines.delete(variantId);
  } else {
    orderLines.set(variantId, { ...meta, variantId, quantity });
  }
  notify();
}

/** @param {string} variantId */
function removeLine(variantId) {
  orderLines.delete(String(variantId));
  notify();
}

/** @param {string} variantId */
function getLineQuantity(variantId) {
  return orderLines.get(String(variantId))?.quantity || 0;
}

// Wholesale minimum order value (USD). Product prices on this page are
// store-currency (USD) cents, so this compares directly against the total.
const WS_ORDER_MINIMUM_CENTS = 40000;

function getTotals() {
  let itemCount = 0;
  let totalCents = 0;
  orderLines.forEach((line) => {
    itemCount += line.quantity;
    totalCents += line.quantity * line.unitPriceCents;
  });
  return { itemCount, totalCents };
}

function formatMoney(cents) {
  return '$' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Plain scrollIntoView({block: 'start'}) aligns an element's own top edge
 * with the very top of the viewport — but the sticky site header sits on
 * top of that, hiding whatever's right at the target's own top (e.g. the
 * "Order Summary" heading, or the Terms & Conditions toggle). Scrolling to
 * an explicit offset position (header height + a little breathing room)
 * instead keeps the target fully visible below the header. Shared by the
 * floating bar's "Submit Order" scroll and the Terms & Conditions
 * checkbox's "view terms" link.
 * @param {HTMLElement} el
 */
function scrollBelowStickyHeader(el, behavior = 'smooth') {
  const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
  const EXTRA_SPACING_PX = 24;
  const targetY = el.getBoundingClientRect().top + window.scrollY - headerHeight - EXTRA_SPACING_PX;
  window.scrollTo({ top: Math.max(0, targetY), behavior });
}

/**
 * Pricing model (matches sections/wholesale-product-modal-content.liquid):
 * variant.price is Shopify's own price, treated as the retail/MSRP price.
 * Wholesale/Each is always 50% of it, calculated here rather than stored
 * separately, so it automatically tracks variant.price if that changes.
 */
function wholesaleCents(variant) {
  return Math.round(variant.price * 0.5);
}

// ---------------------------------------------------------------------
// Shared product modal adapter — redirects its quantity changes into the
// order state above instead of the real cart (see the `adapter` param on
// window.WholesaleProductModal.open in assets/wholesale-product-modal.js).
// ---------------------------------------------------------------------

/**
 * The modal renders real Shopify data server-side per row (SKU, price,
 * product title, finish, size, image — see the data-* attributes added to
 * .wholesale-modal__row in sections/wholesale-product-modal-content.liquid)
 * so metadata for a changed variant is read directly off the modal's own
 * DOM rather than re-fetched separately.
 * @param {string} variantId
 * @returns {Omit<OrderLine, 'quantity'> | null}
 */
function readModalRowMeta(variantId) {
  const row = document.querySelector(`#wholesale-product-modal-content .wholesale-modal__row[data-variant-id="${variantId}"]`);
  if (!(row instanceof HTMLElement)) return null;

  return {
    variantId,
    unitPriceCents: parseInt(row.dataset.unitPrice || '0', 10),
    sku: row.dataset.sku || '',
    productTitle: row.dataset.productTitle || '',
    productHandle: row.dataset.productHandle || '',
    productUrl: row.dataset.productHandle ? `/products/${row.dataset.productHandle}` : '',
    finish: row.dataset.finish || '',
    size: row.dataset.size || '',
    image: row.dataset.image || '',
  };
}

const modalAdapter = {
  onQuantityChange(variantId, quantity) {
    const meta = readModalRowMeta(variantId);
    if (meta) setLineQuantity(meta, quantity);
  },
  getQuantity(variantId) {
    return getLineQuantity(variantId);
  },
};

// Exposed so other scripts on the same page (currently just
// ws-lookbook-spread.js) can route their own product-modal quantity
// changes into this same wholesale cart, without needing to load the rest
// of this file. On a page where this script isn't loaded at all (e.g. the
// standalone WS Lookbook page today), `window.WholesaleOrderCart` simply
// doesn't exist, and whatever reads it falls back to its own default
// behavior — see the corresponding check in ws-lookbook-spread.js.
window.WholesaleOrderCart = { adapter: modalAdapter };

function openDetailsModal(productUrl) {
  if (window.WholesaleProductModal) {
    window.WholesaleProductModal.open(productUrl, modalAdapter);
  }
}

// ---------------------------------------------------------------------
// Product cards
// ---------------------------------------------------------------------

function initCards() {
  const cards = document.querySelectorAll('[data-ws-order-card]');

  cards.forEach((card) => {
    const familyScript = card.querySelector('[data-card-family]');
    if (!familyScript) return;

    /** @type {{ products: Array<{ handle: string, url: string, title: string, swatchImage: string|null, finishLabel: string, mainImage: string, hoverImage: string|null, variants: Array<{ id: number, sku: string, price: number, size: string|null, image: string, available: boolean }> }> }} */
    let family;
    try {
      family = JSON.parse(familyScript.textContent || '{}');
    } catch (error) {
      console.error('WS Order: invalid card family data', error);
      return;
    }
    if (!family.products || family.products.length === 0) return;

    const mainImageEl = card.querySelector('[data-card-image-main]');
    const hoverImageEl = card.querySelector('[data-card-image-hover]');
    const finishSelectorEl = card.querySelector('[data-card-finish-selector]');
    const sizeSelectEl = card.querySelector('[data-card-size-select]');
    const skuEl = card.querySelector('[data-card-sku]');
    const priceEl = card.querySelector('[data-card-price]');
    const qtyValueEl = card.querySelector('[data-card-qty-value]');
    const qtyMinusBtn = card.querySelector('[data-card-qty-minus]');
    const qtyPlusBtn = card.querySelector('[data-card-qty-plus]');
    const lineTotalEl = card.querySelector('[data-card-line-total]');
    const detailsBtn = card.querySelector('[data-card-details]');
    const galleryEl = card.querySelector('[data-card-gallery]');

    let activeFinishIndex = 0;
    let activeVariantId = String(family.products[0].variants[0]?.id || '');

    function activeProduct() {
      return family.products[activeFinishIndex];
    }

    function activeVariant() {
      return activeProduct().variants.find((v) => String(v.id) === activeVariantId) || activeProduct().variants[0];
    }

    function render() {
      const product = activeProduct();
      const variant = activeVariant();
      if (!variant) return;
      activeVariantId = String(variant.id);

      // Main + hover image only (same structure as the regular collection
      // cards) — the full native-order photo gallery lives in the
      // +Details modal, not on the card itself.
      if (mainImageEl) mainImageEl.src = product.mainImage || variant.image;
      if (hoverImageEl) hoverImageEl.src = product.hoverImage || '';

      // Finish swatches (only shown when there's more than one finish)
      if (finishSelectorEl) {
        finishSelectorEl.innerHTML = '';
        if (family.products.length > 1) {
          family.products.forEach((p, index) => {
            const swatch = document.createElement('button');
            swatch.type = 'button';
            swatch.className = 'ws-order-card__finish-swatch' + (index === activeFinishIndex ? ' is-active' : '');
            if (p.swatchImage) swatch.style.backgroundImage = `url(${p.swatchImage})`;
            swatch.setAttribute('aria-label', p.finishLabel);
            swatch.setAttribute('aria-selected', index === activeFinishIndex ? 'true' : 'false');
            swatch.addEventListener('click', () => {
              activeFinishIndex = index;
              // Try to keep the same size selected across finishes.
              const currentSize = variant.size;
              const match = family.products[index].variants.find((v) => v.size === currentSize);
              activeVariantId = String((match || family.products[index].variants[0]).id);
              render();
            });
            finishSelectorEl.appendChild(swatch);
          });
        }
      }

      // Size select (only shown when this finish has more than one distinct size)
      const sizes = product.variants.map((v) => v.size).filter((s, i, arr) => s && arr.indexOf(s) === i);
      if (sizeSelectEl) {
        if (sizes.length > 1) {
          sizeSelectEl.innerHTML = '';
          product.variants.forEach((v) => {
            const option = document.createElement('option');
            option.value = String(v.id);
            option.textContent = v.size || v.sku;
            option.selected = String(v.id) === activeVariantId;
            sizeSelectEl.appendChild(option);
          });
          sizeSelectEl.hidden = false;
          sizeSelectEl.onchange = () => {
            activeVariantId = sizeSelectEl.value;
            render();
          };
        } else {
          sizeSelectEl.hidden = true;
        }
      }

      if (skuEl) skuEl.textContent = `SKU | ${variant.sku}`;
      if (priceEl) priceEl.textContent = `${formatMoney(wholesaleCents(variant))} / each`;

      const quantity = getLineQuantity(activeVariantId);
      if (qtyValueEl) qtyValueEl.textContent = String(quantity);
      if (lineTotalEl) lineTotalEl.textContent = formatMoney(quantity * wholesaleCents(variant));

      if (detailsBtn) detailsBtn.dataset.productUrl = product.url;
    }

    function changeQuantity(delta) {
      const variant = activeVariant();
      if (!variant) return;
      const product = activeProduct();
      const newQuantity = Math.max(0, getLineQuantity(activeVariantId) + delta);

      setLineQuantity(
        {
          variantId: String(variant.id),
          unitPriceCents: wholesaleCents(variant),
          sku: variant.sku,
          productTitle: product.title,
          productHandle: product.handle,
          productUrl: product.url,
          finish: product.finishLabel,
          size: variant.size || '',
          image: variant.image,
        },
        newQuantity
      );
    }

    qtyMinusBtn?.addEventListener('click', () => changeQuantity(-1));
    qtyPlusBtn?.addEventListener('click', () => changeQuantity(1));
    detailsBtn?.addEventListener('click', () => openDetailsModal(activeProduct().url));
    galleryEl?.addEventListener('click', () => openDetailsModal(activeProduct().url));

    subscribe(render);
    render();
  });
}

// ---------------------------------------------------------------------
// Category sidebar filter
// ---------------------------------------------------------------------

function initCategoryFilter() {
  const nav = document.querySelector('[data-ws-order-categories]');
  const grid = document.querySelector('[data-ws-order-grid]');
  if (!nav || !grid) return;

  const buttons = nav.querySelectorAll('[data-category-filter]');
  const cards = grid.querySelectorAll('[data-ws-order-card]');

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const category = button.dataset.categoryFilter;

      buttons.forEach((b) => b.classList.toggle('is-active', b === button));

      cards.forEach((card) => {
        if (category === 'all') {
          card.hidden = false;
          return;
        }
        const categories = (card.dataset.categories || '').split(' ');
        card.hidden = !categories.includes(category);
      });
    });
  });
}

// ---------------------------------------------------------------------
// Floating order bar
// ---------------------------------------------------------------------

function initOrderBar() {
  // Normally exactly one of these per page (standalone WS Lookbook / WS
  // Order), but the combined WS Portal page renders one inside each of
  // its Lookbook/Order sections (see snippets/ws-order-bar.liquid) so
  // each tab has its own — only one is ever actually visible at a time,
  // since the inactive tab's whole section is display:none, but both
  // need to stay in sync so whichever one is currently visible is
  // correct the instant it's revealed.
  const bars = document.querySelectorAll('[data-ws-order-bar]');
  if (bars.length === 0) return;

  // Once the buyer scrolls past the bottom row of product cards there's
  // nothing left to add, so the bar steps aside. Pages with no product
  // grid, or where no card is currently rendered (the Lookbook tab on the
  // portal), never count as "past".
  let pastProductCards = false;

  function render() {
    const { itemCount, totalCents } = getTotals();
    bars.forEach((bar) => {
      const countEl = bar.querySelector('[data-ws-order-bar-count]');
      const totalEl = bar.querySelector('[data-ws-order-bar-total]');
      if (countEl) countEl.textContent = String(itemCount);
      if (totalEl) totalEl.textContent = formatMoney(totalCents);
      bar.hidden = itemCount === 0 || pastProductCards;
    });
  }

  function updatePastProductCards() {
    let lowestBottom = -Infinity;
    document.querySelectorAll('[data-ws-order-card]').forEach((card) => {
      if (card.getClientRects().length === 0) return;
      lowestBottom = Math.max(lowestBottom, card.getBoundingClientRect().bottom);
    });
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const past = lowestBottom !== -Infinity && lowestBottom <= headerHeight;
    if (past !== pastProductCards) {
      pastProductCards = past;
      render();
    }
  }

  let scrollFrame = 0;
  function onScrollOrResize() {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      updatePastProductCards();
    });
  }
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  bars.forEach((bar) => {
    const submitBtn = bar.querySelector('[data-ws-order-bar-submit]');
    submitBtn?.addEventListener('click', () => {
      // On the combined WS Portal page (assets/ws-portal-tabs.js), switches
      // to the Order tab first so the form below is actually visible to
      // scroll to — window.WsPortalTabs simply doesn't exist on either
      // standalone page, leaving this a no-op there (unchanged behavior).
      window.WsPortalTabs?.activate('order');

      const formsSection = document.getElementById('ws-order-forms');
      if (formsSection) scrollBelowStickyHeader(formsSection);
    });
  });

  subscribe(render);
  updatePastProductCards();
  render();
}

// ---------------------------------------------------------------------
// Order Summary (Order & Forms section)
// ---------------------------------------------------------------------

function initOrderSummary() {
  const rowsEl = document.querySelector('[data-ws-order-summary-rows]');
  const emptyEl = document.querySelector('[data-ws-order-summary-empty]');
  const totalRowEl = document.querySelector('[data-ws-order-summary-total-row]');
  const totalEl = document.querySelector('[data-ws-order-summary-total]');
  if (!rowsEl) return;

  function render() {
    rowsEl.innerHTML = '';

    if (orderLines.size === 0) {
      if (emptyEl) emptyEl.hidden = false;
      if (totalRowEl) totalRowEl.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (totalRowEl) totalRowEl.hidden = false;

    orderLines.forEach((line) => {
      const row = document.createElement('div');
      row.className = 'ws-order-summary__row';

      const details = [line.finish, line.size].filter(Boolean).join(' · ');

      row.innerHTML = `
        <img src="${line.image}" alt="${line.productTitle}" loading="lazy" width="64" height="64">
        <div class="ws-order-summary__row-details">
          <p class="ws-order-summary__row-title">${line.productTitle}</p>
          <p>${details}</p>
          <p>SKU | ${line.sku}</p>
        </div>
        <div class="ws-order-summary__row-quantity">
          <button type="button" data-action="decrease" aria-label="Decrease quantity">−</button>
          <span>${line.quantity}</span>
          <button type="button" data-action="increase" aria-label="Increase quantity">+</button>
        </div>
        <div class="ws-order-summary__row-total">${formatMoney(line.quantity * line.unitPriceCents)}</div>
        <button type="button" class="ws-order-summary__row-remove" data-action="remove">Remove</button>
      `;

      row.querySelector('[data-action="decrease"]')?.addEventListener('click', () => {
        setLineQuantity(line, line.quantity - 1);
      });
      row.querySelector('[data-action="increase"]')?.addEventListener('click', () => {
        setLineQuantity(line, line.quantity + 1);
      });
      row.querySelector('[data-action="remove"]')?.addEventListener('click', () => {
        removeLine(line.variantId);
      });

      rowsEl.appendChild(row);
    });

    if (totalEl) totalEl.textContent = formatMoney(getTotals().totalCents);
  }

  subscribe(render);
  render();
}

// ---------------------------------------------------------------------
// Wholesale Order Request form + submission
//
// This is Shopify's own native {% form 'contact' %} (see
// sections/ws-order-forms.liquid) — a real submission to Shopify, not
// JS-intercepted, the same proven mechanism as the sidebar "Have
// questions?" form. This script's job is only to: (1) persist
// partially-filled field values across the real page round-trip, (2)
// toggle the two "same as" checkboxes' dependent fields, (3) assemble the
// full company/contact/product breakdown into the hidden contact[body]
// field right before the real submit happens, and (4) detect a confirmed
// successful return (via the same return_to + marker technique as the
// sidebar form) to show the confirmation and clear the cart/draft — never
// before that confirmed point.
// ---------------------------------------------------------------------

// Entered field values only (no products) — sessionStorage, not
// localStorage, since this is personal/company information: it survives
// refreshes and the submission round-trip within the same browser tab
// session, but is never kept permanently and disappears once the tab/
// browser closes. Cleared explicitly only once a submission is confirmed
// successful.
const WS_ORDER_FORM_DRAFT_KEY = 'wsOrderFormDraft';
const WS_ORDER_REQUEST_REF_KEY = 'wsOrderRequestReference';

/** @param {HTMLFormElement} form */
function serializeOrderFormDraft(form) {
  /** @type {Record<string, string | boolean>} */
  const data = {};
  Array.from(form.elements).forEach((el) => {
    if (!('name' in el) || !el.name || el.name === 'contact[body]' || el.name === 'contact[phone]' || el.name === 'return_to') return;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      data[el.name] = el.checked;
    } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      data[el.name] = el.value;
    }
  });
  return data;
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, string | boolean>} data
 */
function restoreOrderFormDraft(form, data) {
  Object.keys(data).forEach((name) => {
    const el = form.elements.namedItem(name);
    if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) return;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      el.checked = Boolean(data[name]);
    } else {
      el.value = String(data[name]);
    }
  });
}

/** @param {HTMLFormElement} form */
function initOrderFormDraftPersistence(form) {
  try {
    const raw = sessionStorage.getItem(WS_ORDER_FORM_DRAFT_KEY);
    if (raw) restoreOrderFormDraft(form, JSON.parse(raw));
  } catch (error) {
    // Ignore corrupt/unavailable storage — form just starts blank.
  }

  const saveDraft = () => {
    try {
      sessionStorage.setItem(WS_ORDER_FORM_DRAFT_KEY, JSON.stringify(serializeOrderFormDraft(form)));
    } catch (error) {
      // Storage can be unavailable — the form still works either way.
    }
  };
  form.addEventListener('input', saveDraft);
  form.addEventListener('change', saveDraft);
}

/**
 * Wires a "Same as X" checkbox to hide/show (and require/not-require) a
 * group of dependent fields (except any marked data-ws-order-optional).
 * Applied once immediately so a restored draft
 * (checkbox already checked) renders in the correct state on load.
 * @param {HTMLFormElement} form
 * @param {string} checkboxName
 * @param {string} fieldsAttribute
 */
function initSameAsToggle(form, checkboxName, fieldsAttribute) {
  const checkbox = form.elements.namedItem(checkboxName);
  const wrap = form.querySelector(`[${fieldsAttribute}]`);
  if (!(checkbox instanceof HTMLInputElement) || !wrap) return;

  const fields = wrap.querySelectorAll('input, textarea, select');

  function apply() {
    const same = checkbox.checked;
    wrap.hidden = same;
    fields.forEach((field) => {
      // Fields marked data-ws-order-optional (e.g. apartment/suite/unit)
      // stay optional whether or not the group is shown.
      if (same || field.hasAttribute('data-ws-order-optional')) {
        field.removeAttribute('required');
      } else {
        field.setAttribute('required', 'required');
      }
    });
  }

  checkbox.addEventListener('change', apply);
  apply();
}

// Full ISO 3166-1 country list with E.164 calling codes — deliberately
// NOT sourced from Shopify's own `localization.available_countries`
// Liquid object, since that's restricted to whichever countries are
// enabled in this store's Markets settings. Wholesale inquiries can come
// from anywhere, so this static table (populated into both country
// selects via JS) always offers every country regardless of Markets
// configuration. Sorted alphabetically by name for the dropdown.
const WS_ORDER_COUNTRIES = [
  { code: 'AF', name: 'Afghanistan', dialCode: '+93' },
  { code: 'AL', name: 'Albania', dialCode: '+355' },
  { code: 'DZ', name: 'Algeria', dialCode: '+213' },
  { code: 'AD', name: 'Andorra', dialCode: '+376' },
  { code: 'AO', name: 'Angola', dialCode: '+244' },
  { code: 'AG', name: 'Antigua and Barbuda', dialCode: '+1' },
  { code: 'AR', name: 'Argentina', dialCode: '+54' },
  { code: 'AM', name: 'Armenia', dialCode: '+374' },
  { code: 'AU', name: 'Australia', dialCode: '+61' },
  { code: 'AT', name: 'Austria', dialCode: '+43' },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '+994' },
  { code: 'BS', name: 'Bahamas', dialCode: '+1' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { code: 'BB', name: 'Barbados', dialCode: '+1' },
  { code: 'BY', name: 'Belarus', dialCode: '+375' },
  { code: 'BE', name: 'Belgium', dialCode: '+32' },
  { code: 'BZ', name: 'Belize', dialCode: '+501' },
  { code: 'BJ', name: 'Benin', dialCode: '+229' },
  { code: 'BT', name: 'Bhutan', dialCode: '+975' },
  { code: 'BO', name: 'Bolivia', dialCode: '+591' },
  { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387' },
  { code: 'BW', name: 'Botswana', dialCode: '+267' },
  { code: 'BR', name: 'Brazil', dialCode: '+55' },
  { code: 'BN', name: 'Brunei', dialCode: '+673' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226' },
  { code: 'BI', name: 'Burundi', dialCode: '+257' },
  { code: 'KH', name: 'Cambodia', dialCode: '+855' },
  { code: 'CM', name: 'Cameroon', dialCode: '+237' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'CV', name: 'Cape Verde', dialCode: '+238' },
  { code: 'CF', name: 'Central African Republic', dialCode: '+236' },
  { code: 'TD', name: 'Chad', dialCode: '+235' },
  { code: 'CL', name: 'Chile', dialCode: '+56' },
  { code: 'CN', name: 'China', dialCode: '+86' },
  { code: 'CO', name: 'Colombia', dialCode: '+57' },
  { code: 'KM', name: 'Comoros', dialCode: '+269' },
  { code: 'CG', name: 'Congo', dialCode: '+242' },
  { code: 'CD', name: 'Congo (DRC)', dialCode: '+243' },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506' },
  { code: 'HR', name: 'Croatia', dialCode: '+385' },
  { code: 'CU', name: 'Cuba', dialCode: '+53' },
  { code: 'CY', name: 'Cyprus', dialCode: '+357' },
  { code: 'CZ', name: 'Czechia', dialCode: '+420' },
  { code: 'DK', name: 'Denmark', dialCode: '+45' },
  { code: 'DJ', name: 'Djibouti', dialCode: '+253' },
  { code: 'DM', name: 'Dominica', dialCode: '+1' },
  { code: 'DO', name: 'Dominican Republic', dialCode: '+1' },
  { code: 'EC', name: 'Ecuador', dialCode: '+593' },
  { code: 'EG', name: 'Egypt', dialCode: '+20' },
  { code: 'SV', name: 'El Salvador', dialCode: '+503' },
  { code: 'GQ', name: 'Equatorial Guinea', dialCode: '+240' },
  { code: 'ER', name: 'Eritrea', dialCode: '+291' },
  { code: 'EE', name: 'Estonia', dialCode: '+372' },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251' },
  { code: 'FJ', name: 'Fiji', dialCode: '+679' },
  { code: 'FI', name: 'Finland', dialCode: '+358' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'GA', name: 'Gabon', dialCode: '+241' },
  { code: 'GM', name: 'Gambia', dialCode: '+220' },
  { code: 'GE', name: 'Georgia', dialCode: '+995' },
  { code: 'DE', name: 'Germany', dialCode: '+49' },
  { code: 'GH', name: 'Ghana', dialCode: '+233' },
  { code: 'GR', name: 'Greece', dialCode: '+30' },
  { code: 'GD', name: 'Grenada', dialCode: '+1' },
  { code: 'GT', name: 'Guatemala', dialCode: '+502' },
  { code: 'GN', name: 'Guinea', dialCode: '+224' },
  { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245' },
  { code: 'GY', name: 'Guyana', dialCode: '+592' },
  { code: 'HT', name: 'Haiti', dialCode: '+509' },
  { code: 'HN', name: 'Honduras', dialCode: '+504' },
  { code: 'HK', name: 'Hong Kong SAR', dialCode: '+852' },
  { code: 'HU', name: 'Hungary', dialCode: '+36' },
  { code: 'IS', name: 'Iceland', dialCode: '+354' },
  { code: 'IN', name: 'India', dialCode: '+91' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62' },
  { code: 'IR', name: 'Iran', dialCode: '+98' },
  { code: 'IQ', name: 'Iraq', dialCode: '+964' },
  { code: 'IE', name: 'Ireland', dialCode: '+353' },
  { code: 'IL', name: 'Israel', dialCode: '+972' },
  { code: 'IT', name: 'Italy', dialCode: '+39' },
  { code: 'JM', name: 'Jamaica', dialCode: '+1' },
  { code: 'JP', name: 'Japan', dialCode: '+81' },
  { code: 'JO', name: 'Jordan', dialCode: '+962' },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7' },
  { code: 'KE', name: 'Kenya', dialCode: '+254' },
  { code: 'KI', name: 'Kiribati', dialCode: '+686' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965' },
  { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996' },
  { code: 'LA', name: 'Laos', dialCode: '+856' },
  { code: 'LV', name: 'Latvia', dialCode: '+371' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961' },
  { code: 'LS', name: 'Lesotho', dialCode: '+266' },
  { code: 'LR', name: 'Liberia', dialCode: '+231' },
  { code: 'LY', name: 'Libya', dialCode: '+218' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423' },
  { code: 'LT', name: 'Lithuania', dialCode: '+370' },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352' },
  { code: 'MO', name: 'Macao SAR', dialCode: '+853' },
  { code: 'MG', name: 'Madagascar', dialCode: '+261' },
  { code: 'MW', name: 'Malawi', dialCode: '+265' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { code: 'MV', name: 'Maldives', dialCode: '+960' },
  { code: 'ML', name: 'Mali', dialCode: '+223' },
  { code: 'MT', name: 'Malta', dialCode: '+356' },
  { code: 'MH', name: 'Marshall Islands', dialCode: '+692' },
  { code: 'MR', name: 'Mauritania', dialCode: '+222' },
  { code: 'MU', name: 'Mauritius', dialCode: '+230' },
  { code: 'MX', name: 'Mexico', dialCode: '+52' },
  { code: 'FM', name: 'Micronesia', dialCode: '+691' },
  { code: 'MD', name: 'Moldova', dialCode: '+373' },
  { code: 'MC', name: 'Monaco', dialCode: '+377' },
  { code: 'MN', name: 'Mongolia', dialCode: '+976' },
  { code: 'ME', name: 'Montenegro', dialCode: '+382' },
  { code: 'MA', name: 'Morocco', dialCode: '+212' },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258' },
  { code: 'MM', name: 'Myanmar', dialCode: '+95' },
  { code: 'NA', name: 'Namibia', dialCode: '+264' },
  { code: 'NR', name: 'Nauru', dialCode: '+674' },
  { code: 'NP', name: 'Nepal', dialCode: '+977' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505' },
  { code: 'NE', name: 'Niger', dialCode: '+227' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234' },
  { code: 'MK', name: 'North Macedonia', dialCode: '+389' },
  { code: 'NO', name: 'Norway', dialCode: '+47' },
  { code: 'OM', name: 'Oman', dialCode: '+968' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92' },
  { code: 'PW', name: 'Palau', dialCode: '+680' },
  { code: 'PS', name: 'Palestine', dialCode: '+970' },
  { code: 'PA', name: 'Panama', dialCode: '+507' },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675' },
  { code: 'PY', name: 'Paraguay', dialCode: '+595' },
  { code: 'PE', name: 'Peru', dialCode: '+51' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'PL', name: 'Poland', dialCode: '+48' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'QA', name: 'Qatar', dialCode: '+974' },
  { code: 'RO', name: 'Romania', dialCode: '+40' },
  { code: 'RU', name: 'Russia', dialCode: '+7' },
  { code: 'RW', name: 'Rwanda', dialCode: '+250' },
  { code: 'KN', name: 'Saint Kitts and Nevis', dialCode: '+1' },
  { code: 'LC', name: 'Saint Lucia', dialCode: '+1' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', dialCode: '+1' },
  { code: 'WS', name: 'Samoa', dialCode: '+685' },
  { code: 'SM', name: 'San Marino', dialCode: '+378' },
  { code: 'ST', name: 'Sao Tome and Principe', dialCode: '+239' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { code: 'SN', name: 'Senegal', dialCode: '+221' },
  { code: 'RS', name: 'Serbia', dialCode: '+381' },
  { code: 'SC', name: 'Seychelles', dialCode: '+248' },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232' },
  { code: 'SG', name: 'Singapore', dialCode: '+65' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421' },
  { code: 'SI', name: 'Slovenia', dialCode: '+386' },
  { code: 'SB', name: 'Solomon Islands', dialCode: '+677' },
  { code: 'SO', name: 'Somalia', dialCode: '+252' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'KR', name: 'South Korea', dialCode: '+82' },
  { code: 'SS', name: 'South Sudan', dialCode: '+211' },
  { code: 'ES', name: 'Spain', dialCode: '+34' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94' },
  { code: 'SD', name: 'Sudan', dialCode: '+249' },
  { code: 'SR', name: 'Suriname', dialCode: '+597' },
  { code: 'SE', name: 'Sweden', dialCode: '+46' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41' },
  { code: 'SY', name: 'Syria', dialCode: '+963' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886' },
  { code: 'TJ', name: 'Tajikistan', dialCode: '+992' },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255' },
  { code: 'TH', name: 'Thailand', dialCode: '+66' },
  { code: 'TL', name: 'Timor-Leste', dialCode: '+670' },
  { code: 'TG', name: 'Togo', dialCode: '+228' },
  { code: 'TO', name: 'Tonga', dialCode: '+676' },
  { code: 'TT', name: 'Trinidad and Tobago', dialCode: '+1' },
  { code: 'TN', name: 'Tunisia', dialCode: '+216' },
  { code: 'TR', name: 'Turkey', dialCode: '+90' },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993' },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688' },
  { code: 'UG', name: 'Uganda', dialCode: '+256' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { code: 'US', name: 'United States', dialCode: '+1' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598' },
  { code: 'UZ', name: 'Uzbekistan', dialCode: '+998' },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678' },
  { code: 'VA', name: 'Vatican City', dialCode: '+379' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84' },
  { code: 'YE', name: 'Yemen', dialCode: '+967' },
  { code: 'ZM', name: 'Zambia', dialCode: '+260' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263' },
];

/** @param {HTMLFormElement} form */
function populateCountrySelects(form) {
  form.querySelectorAll('[data-ws-order-country-select]').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    WS_ORDER_COUNTRIES.forEach((country) => {
      const option = document.createElement('option');
      option.value = country.code;
      option.textContent = country.name;
      select.appendChild(option);
    });
  });
}

/** @param {string} code */
function getCountryByCode(code) {
  return WS_ORDER_COUNTRIES.find((c) => c.code === code) || null;
}

/**
 * Keeps each phone field's dial-code display in sync with its paired
 * country select, without ever touching the actual phone number input —
 * the two are kept as separate fields/values internally (country select,
 * dial-code display, local number input) and only combined into one
 * string at submit time (see buildOrderRequestEmailBody /
 * initOrderForm's submit handler). This is what lets the dial code update
 * freely when the country changes without ever deleting digits the buyer
 * already typed into the number field.
 * @param {HTMLFormElement} form
 */
function initCountryDialCodes(form) {
  form.querySelectorAll('[data-ws-order-dialcode-for]').forEach((dialcodeEl) => {
    const countryFieldName = dialcodeEl.getAttribute('data-ws-order-dialcode-for');
    const countrySelect = countryFieldName ? form.elements.namedItem(countryFieldName) : null;
    if (!(countrySelect instanceof HTMLSelectElement)) return;

    function apply() {
      const country = getCountryByCode(countrySelect.value);
      dialcodeEl.textContent = country ? country.dialCode : '';
    }

    countrySelect.addEventListener('change', apply);
    apply();
  });

  initShippingContactDialCode(form);
}

/**
 * Which country the Shipping Address section's own country effectively
 * is right now: Company Country when "Same as company address" is
 * checked (the Shipping Country select is hidden/blank in that case), its
 * own Shipping Country select otherwise. Shared by the live Shipping
 * Contact Phone dial-code display below and by
 * resolveShippingAddressLines/resolveShippingContact in the email body builder,
 * so both always agree.
 * @param {HTMLFormElement} form
 */
function getEffectiveShippingAddressCountryCode(form) {
  const addressSame = form.elements.namedItem('shippingSameAsCompany');
  if (addressSame instanceof HTMLInputElement && addressSame.checked) {
    return form.elements.namedItem('companyCountry')?.value || '';
  }
  return form.elements.namedItem('shippingCountry')?.value || '';
}

/**
 * Which country the Shipping Contact's own phone dial code should show:
 * Company Country when "Same as primary contact" is checked (mirroring
 * the primary contact's own phone, which is always Company Country), or
 * whatever the Shipping Address section's effective country is otherwise
 * (which itself falls back to Company Country when "Same as company
 * address" is checked) — covering all four checkbox combinations.
 * @param {HTMLFormElement} form
 */
function getEffectiveShippingContactCountryCode(form) {
  const contactSame = form.elements.namedItem('shippingContactSame');
  if (contactSame instanceof HTMLInputElement && contactSame.checked) {
    return form.elements.namedItem('companyCountry')?.value || '';
  }
  return getEffectiveShippingAddressCountryCode(form);
}

/**
 * The Shipping Contact Phone's dial-code display can't use the generic
 * one-select-per-span wiring above, since the correct country depends on
 * two checkboxes as well as two country selects (see
 * getEffectiveShippingContactCountryCode) — it needs to stay correct and
 * already filled in the instant the buyer reaches this field, in every
 * combination of "Same as company address" / "Same as primary contact".
 * @param {HTMLFormElement} form
 */
function initShippingContactDialCode(form) {
  const dialcodeEl = form.querySelector('[data-ws-order-shipping-contact-dialcode]');
  if (!dialcodeEl) return;

  function apply() {
    const country = getCountryByCode(getEffectiveShippingContactCountryCode(form));
    dialcodeEl.textContent = country ? country.dialCode : '';
  }

  ['companyCountry', 'shippingCountry', 'shippingSameAsCompany', 'shippingContactSame'].forEach((name) => {
    form.elements.namedItem(name)?.addEventListener('change', apply);
  });
  apply();
}

function getOrCreateRequestReference() {
  try {
    const existing = sessionStorage.getItem(WS_ORDER_REQUEST_REF_KEY);
    if (existing) return existing;
    const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomPart = Math.random().toString(36).slice(2, 7).toUpperCase();
    const reference = `WS-${datePart}-${randomPart}`;
    sessionStorage.setItem(WS_ORDER_REQUEST_REF_KEY, reference);
    return reference;
  } catch (error) {
    // No storage available — still return a usable (if non-persisted)
    // reference for this one submission.
    return `WS-${Date.now()}`;
  }
}

/**
 * Combines a dial code and a local number into one display string without
 * ever storing them together in the actual input — called only at the
 * point of building the email (and the hidden contact[phone] field), so
 * changing the country never has to parse a prefix back out of whatever
 * the buyer already typed.
 * @param {string | null | undefined} dialCode
 * @param {string | null | undefined} number
 */
function combinePhone(dialCode, number) {
  const trimmedNumber = String(number || '').trim();
  if (!trimmedNumber) return dialCode || '';
  return dialCode ? `${dialCode} ${trimmedNumber}` : trimmedNumber;
}

/**
 * @param {FormData} formData
 * @param {string} prefix  'company' or 'shipping' — field-name prefix
 * @param {string} countryName
 * @param {string} [recipient]  Shipping addresses only
 * @returns {string[]}
 */
function buildAddressLines(formData, prefix, countryName, recipient) {
  const get = (suffix) => String(formData.get(`${prefix}${suffix}`) || '').trim();
  const lines = [];
  if (recipient) lines.push(`Recipient / Company: ${recipient}`);
  lines.push(`Street Address: ${get('Street')}`);
  if (get('Apartment')) lines.push(`Apartment / Suite / Unit: ${get('Apartment')}`);
  lines.push(`City: ${get('City')}`);
  if (get('State')) lines.push(`State / Province / Region: ${get('State')}`);
  lines.push(`Postal / ZIP Code: ${get('PostalCode')}`, `Country: ${countryName}`);
  return lines;
}

/**
 * @param {FormData} formData
 * @param {HTMLFormElement} form
 * @returns {string[]}
 */
function resolveShippingAddressLines(formData, form) {
  const same = form.elements.namedItem('shippingSameAsCompany');
  const countryName = getCountryByCode(getEffectiveShippingAddressCountryCode(form))?.name || '';
  if (same instanceof HTMLInputElement && same.checked) {
    return buildAddressLines(formData, 'company', countryName, String(formData.get('companyName') || '').trim());
  }
  return buildAddressLines(formData, 'shipping', countryName, String(formData.get('shippingRecipient') || '').trim());
}

/**
 * @param {FormData} formData
 * @param {string} firstNameField
 * @param {string} lastNameField
 */
function combineName(formData, firstNameField, lastNameField) {
  return [formData.get(firstNameField), formData.get(lastNameField)]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * @param {FormData} formData
 * @param {HTMLFormElement} form
 */
function resolveShippingContact(formData, form) {
  const same = form.elements.namedItem('shippingContactSame');
  if (same instanceof HTMLInputElement && same.checked) {
    const companyCountry = getCountryByCode(String(formData.get('companyCountry') || ''));
    return {
      name: combineName(formData, 'contactFirstName', 'contactLastName'),
      email: String(formData.get('contact[email]') || ''),
      phone: combinePhone(companyCountry?.dialCode, formData.get('contactPhoneNumber')),
    };
  }
  // "Different shipping contact" still needs to fall back to Company
  // Country when "Same as company address" is checked (the Shipping
  // Country select is hidden/blank in that case) — see
  // getEffectiveShippingAddressCountryCode.
  const effectiveCountry = getCountryByCode(getEffectiveShippingAddressCountryCode(form));
  return {
    name: combineName(formData, 'shippingContactFirstName', 'shippingContactLastName'),
    email: String(formData.get('shippingContactEmail') || ''),
    phone: combinePhone(effectiveCountry?.dialCode, formData.get('shippingContactPhoneNumber')),
  };
}

/** @param {HTMLFormElement} form */
function buildOrderRequestEmailBody(form) {
  const formData = new FormData(form);
  const reference = getOrCreateRequestReference();
  const companyCountry = getCountryByCode(String(formData.get('companyCountry') || ''));
  const primaryContactPhone = combinePhone(companyCountry?.dialCode, formData.get('contactPhoneNumber'));
  const shippingContact = resolveShippingContact(formData, form);
  const { itemCount, totalCents } = getTotals();

  const lines = [
    'WHOLESALE ORDER REQUEST',
    `Reference: ${reference}`,
    `Submitted: ${new Date().toISOString()}`,
    '',
    'COMPANY DETAILS',
    `Company Name: ${formData.get('companyName') || ''}`,
    '',
    'PRIMARY CONTACT',
    `Name: ${combineName(formData, 'contactFirstName', 'contactLastName')}`,
    `Email: ${formData.get('contact[email]') || ''}`,
    `Phone: ${primaryContactPhone}`,
    '',
    'COMPANY ADDRESS',
    ...buildAddressLines(formData, 'company', companyCountry?.name || ''),
    '',
    'SHIPPING ADDRESS',
    ...resolveShippingAddressLines(formData, form),
    '',
    'SHIPPING CONTACT',
    `Name: ${shippingContact.name}`,
    `Email: ${shippingContact.email}`,
    `Phone: ${shippingContact.phone}`,
    '',
    `Currency: ${formData.get('currency') || ''}`,
    `Payment Method: ${formData.get('paymentPreference') || ''}`,
  ];

  const notes = formData.get('notes');
  if (notes) {
    lines.push('', 'NOTES', String(notes));
  }

  lines.push('', '----------------------------------------', 'PRODUCT SELECTIONS', '----------------------------------------');

  let index = 1;
  orderLines.forEach((line) => {
    const details = [line.finish, line.size].filter(Boolean).join(', ');
    lines.push(`${index}. ${line.productTitle}${details ? ' — ' + details : ''}`);
    lines.push(
      `   SKU: ${line.sku} | Qty: ${line.quantity} | Unit: ${formatMoney(line.unitPriceCents)} | Line total: ${formatMoney(
        line.quantity * line.unitPriceCents
      )}`
    );
    index += 1;
  });

  lines.push('', `ORDER TOTAL: ${formatMoney(totalCents)}`, `Total items: ${itemCount}`);

  return lines.join('\n');
}

/** @returns {string} Empty when the order can be submitted. */
function getOrderBlockMessage() {
  if (orderLines.size === 0) return 'Add at least one product before submitting your order.';
  const { totalCents } = getTotals();
  if (totalCents < WS_ORDER_MINIMUM_CENTS) {
    return `The minimum order value is USD ${WS_ORDER_MINIMUM_CENTS / 100}. Your current total is ${formatMoney(
      totalCents
    )} — please add ${formatMoney(WS_ORDER_MINIMUM_CENTS - totalCents)} more to submit your order.`;
  }
  return '';
}

/**
 * @param {Element | null} errorEl
 * @param {string} message
 */
function showOrderBlockError(errorEl, message) {
  if (!(errorEl instanceof HTMLElement)) return;
  errorEl.textContent = message;
  errorEl.dataset.wsOrderBlockError = 'true';
  errorEl.hidden = false;
}

function initOrderForm() {
  const form = document.querySelector('[data-ws-order-form]');
  const errorEl = document.querySelector('[data-ws-order-form-error]');
  if (!(form instanceof HTMLFormElement)) return;

  const bodyField = form.elements.namedItem('contact[body]');
  const phoneField = form.elements.namedItem('contact[phone]');

  // Country selects must be populated before the draft restore below (so
  // a restored select value has a matching <option> to land on), and the
  // dial-code displays must be initialized after it (so a restored
  // country selection is reflected immediately).
  populateCountrySelects(form);
  initOrderFormDraftPersistence(form);
  initCountryDialCodes(form);
  initSameAsToggle(form, 'shippingSameAsCompany', 'data-ws-order-shipping-address-field');
  initSameAsToggle(form, 'shippingContactSame', 'data-ws-order-shipping-contact-fields');

  // The order-can't-be-sent states (empty cart, under the minimum) are
  // enforced through native constraint validation on a hidden guard input,
  // exactly like an empty required field: the browser then never fires the
  // form's submit event at all. A plain preventDefault() in a submit handler
  // is not enough here — Shopify's spam-protection/forms scripts also listen
  // for submit and can still send the form afterwards, which would deliver
  // an order with no email body or reference.
  const guard = form.querySelector('[data-ws-order-guard]');
  function syncGuard() {
    if (guard instanceof HTMLInputElement) guard.setCustomValidity(getOrderBlockMessage());
    if (errorEl && errorEl.dataset.wsOrderBlockError && !getOrderBlockMessage()) {
      errorEl.hidden = true;
      delete errorEl.dataset.wsOrderBlockError;
    }
  }
  guard?.addEventListener('invalid', () => showOrderBlockError(errorEl, getOrderBlockMessage()));
  subscribe(syncGuard);
  syncGuard();

  form.addEventListener('submit', (event) => {
    // Native required-field validation already ran (this form has no
    // novalidate) — by the time this handler runs, every required field
    // is already valid. This is the one condition HTML can't express.
    if (errorEl) {
      errorEl.hidden = true;
      delete errorEl.dataset.wsOrderBlockError;
    }

    // Backstop only: the guard input below normally stops the browser from
    // ever dispatching this submit event while the order can't be sent.
    const blockMessage = getOrderBlockMessage();
    if (blockMessage) {
      event.preventDefault();
      showOrderBlockError(errorEl, blockMessage);
      return;
    }

    if (phoneField instanceof HTMLInputElement) {
      const formData = new FormData(form);
      const companyCountry = getCountryByCode(String(formData.get('companyCountry') || ''));
      phoneField.value = combinePhone(companyCountry?.dialCode, formData.get('contactPhoneNumber'));
    }

    if (bodyField instanceof HTMLTextAreaElement) {
      bodyField.value = buildOrderRequestEmailBody(form);
    }

    // Disabling here (right before the real, un-intercepted submission
    // proceeds) prevents a double-click from firing a second submission
    // during the navigation — the cart/draft themselves are only ever
    // cleared later, from the confirmed-success return (see
    // initOrderFormSuccess), never from this handler.
    const submitBtn = form.querySelector('.ws-order-form__submit');
    if (submitBtn instanceof HTMLButtonElement) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
    }
    // No preventDefault() here — the real native submission to Shopify
    // proceeds normally from this point.
  });
}

// ---------------------------------------------------------------------
// Wholesale Order Request — confirmed-success return.
//
// Mirrors initSidebarContactSuccess below exactly, with its own distinct
// `ws_order_sent` marker so the two native contact forms on this page
// never get confused with one another. This is also the ONLY place the
// cart and the saved form draft/reference are cleared — never
// optimistically on submit — so an interrupted submission (closed tab,
// failed network request, an hCaptcha challenge the buyer abandons) never
// loses their selections or entered details.
// ---------------------------------------------------------------------

function initOrderFormSuccess() {
  if (!window.location.search.includes('ws_order_sent=true')) return;

  const activeEl = document.querySelector('[data-ws-order-forms-active]');
  const confirmationEl = document.querySelector('[data-ws-order-confirmation]');
  const referenceEl = document.querySelector('[data-ws-order-confirmation-reference]');

  let reference = '';
  try {
    reference = sessionStorage.getItem(WS_ORDER_REQUEST_REF_KEY) || '';
  } catch (error) {
    // No reference to show if storage isn't available — confirmation
    // text alone still displays.
  }

  if (activeEl instanceof HTMLElement) activeEl.hidden = true;
  if (confirmationEl instanceof HTMLElement) {
    confirmationEl.hidden = false;
    // The return trip from Shopify is a full page load. On the portal that
    // load starts on the Lookbook tab at the top (see assets/ws-portal-tabs.js),
    // which would hide this confirmation entirely — so switch to the Order
    // tab (a no-op on the standalone page) and bring the confirmation into
    // view. Repeated once on window load in case images above it finish
    // loading and shift the layout.
    window.WsPortalTabs?.activate('order');
    const reveal = () => scrollBelowStickyHeader(confirmationEl, 'instant');
    reveal();
    if (document.readyState !== 'complete') window.addEventListener('load', reveal, { once: true });
  }
  if (referenceEl instanceof HTMLElement && reference) {
    // `reference` is always our own generated value (never user input —
    // see getOrCreateRequestReference), so innerHTML here is safe.
    referenceEl.innerHTML = `<strong>Reference:</strong> ${reference}`;
  }

  orderLines.clear();
  clearPersistedOrderLines();
  notify();

  try {
    sessionStorage.removeItem(WS_ORDER_FORM_DRAFT_KEY);
    sessionStorage.removeItem(WS_ORDER_REQUEST_REF_KEY);
  } catch (error) {
    // Nothing to do if storage isn't available.
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('ws_order_sent');
  window.history.replaceState({}, '', url.pathname + url.search + window.location.hash);
}

// ---------------------------------------------------------------------
// The sidebar "Have questions?" contact form is Shopify's native
// {% form 'contact' %} (see ws-order-browser.liquid) — a real full-page
// submission, deliberately not intercepted with JS. Shopify's contact form
// has built-in hCaptcha spam protection that only works through the real
// native submission flow; posting to it via fetch() bypasses that and gets
// rejected (confirmed: a 403 from /contact), so the message never actually
// arrives. The `return_to` field on that form sends the visitor back to
// this same page with a `?contact_sent=true` marker we set ourselves
// (Shopify's own posted_successfully?/form.id flag doesn't reliably
// survive the extra redirect hop hCaptcha's interactive challenge adds).
// ---------------------------------------------------------------------

function initSidebarContactSuccess() {
  if (!window.location.search.includes('contact_sent=true')) return;

  const formWrap = document.querySelector('[data-contact-form-wrap]');
  const successMessage = document.querySelector('[data-contact-success]');
  if (formWrap) formWrap.hidden = true;
  if (successMessage) successMessage.hidden = false;

  // Drop the marker from the URL so reloading/sharing this page afterward
  // doesn't keep showing the success state.
  const url = new URL(window.location.href);
  url.searchParams.delete('contact_sent');
  window.history.replaceState({}, '', url.pathname + url.search + window.location.hash);
}

document.addEventListener('DOMContentLoaded', () => {
  restoreOrderLines();
  initCards();
  initCategoryFilter();
  initOrderBar();
  initOrderSummary();
  initOrderForm();
  initOrderFormSuccess();
  initSidebarContactSuccess();
});
